import { createFileRoute, Link } from '@tanstack/react-router'
import type { ColDef } from 'ag-grid-community'
import { icons as lucideIcons } from 'lucide-react'
import { useMemo, useState } from 'react'

import { useLinkDeviceToPointOfSale, useUnlinkDeviceFromPointOfSale, useUpdatePointOfSale } from '@/api/api-mutations'
import {
	preloadGetPointOfSale,
	preloadListDevices,
	preloadListProductGroupsForPointOfSale,
	useGetPointOfSale,
	useListDevices,
	useListSalesChannels,
	useListProductGroupsForPointOfSale
} from '@/api/react-query'
import { AssetImage } from '@/components/base/asset-image'
import { Badge } from '@/components/base/badge'
import { Button } from '@/components/base/button'
import { Container, ContainerHeader } from '@/components/base/container'
import { Drawer } from '@/components/base/drawer'
import { EmptyState } from '@/components/base/empty-state'
import { Heading } from '@/components/base/heading'
import { IconButton } from '@/components/base/icon-button'
import { usePrompt } from '@/components/base/prompt'
import { SectionRow } from '@/components/base/section-row'
import { StatusBadge } from '@/components/base/status-badge'
import { Text } from '@/components/base/text'
import { toast } from '@/components/base/toast'
import { Tooltip } from '@/components/base/tooltip'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { SingleColumnPage } from '@/components/page-layout'
import { Table } from '@/components/table/table'
import { useI18n } from '@/i18n/i18n-react'
import { PencilSquare } from '@/icons/pencil-square'
import { Photo } from '@/icons/photo'
import { ConditionalRouteComponent } from '@/utils/route-utilities'

const kebabToPascal = (value: string) =>
	value
		.split('-')
		.map(part => part.charAt(0).toUpperCase() + part.slice(1))
		.join('')

function ProductGroupGlyph({ name }: { name: string }) {
	const Glyph = lucideIcons[kebabToPascal(name) as keyof typeof lucideIcons]
	if (!Glyph) {
		return <Photo className='text-ui-fg-muted' />
	}
	return <Glyph className='size-5 text-ui-fg-subtle' />
}

export const Route = createFileRoute('/_authenticated/settings/points-of-sale/$pointOfSaleUuid')({
	component: () => (
		<ConditionalRouteComponent routePath={Route.id} pathsToRenderOn={['/edit', '/product-menu']}>
			<PointOfSaleDetail />
		</ConditionalRouteComponent>
	),
	loader: async ({ context, params: { pointOfSaleUuid } }) => {
		const [pointOfSale] = await Promise.all([
			preloadGetPointOfSale(context, { path: { pointOfSaleUuid } }),
			preloadListProductGroupsForPointOfSale(context, {
				path: { pointOfSaleUuid },
				query: { limit: 100, offset: 0 }
			}),
			preloadListDevices(context, {})
		])
		return pointOfSale
	},
	staticData: {
		crumb: ({ loaderData, context: { L } }) => loaderData?.name || L.Settings.PointOfSale.Title()
	}
})

function PointOfSaleDetail() {
	const { L } = useI18n()
	const { pointOfSaleUuid } = Route.useParams()

	const navigate = Route.useNavigate()
	const pointOfSale = useGetPointOfSale({ pointOfSaleUuid })
	const productGroups = useListProductGroupsForPointOfSale({ pointOfSaleUuid }, { limit: 100, offset: 0 })
	const [linkSalesChannelOpen, setLinkSalesChannelOpen] = useState(false)

	const salesChannelColumnDefs = useMemo<ColDef[]>(
		() => [
			{
				headerName: L.Fields.Name(),
				field: 'name',
				flex: 2
			}
		],
		[L]
	)

	if (!pointOfSale.data) {
		return null
	}

	return (
		<SingleColumnPage hasOutlet>
			<Container>
				<ContainerHeader
					heading={pointOfSale.data.name}
					rightSide={
						<Link from={Route.fullPath} to='./edit'>
							<IconButton tooltipText={L.General.Edit()}>
								<PencilSquare />
							</IconButton>
						</Link>
					}
				/>
				<SectionRow
					title={L.Settings.PointOfSale.Column.Group()}
					value={
						pointOfSale.data.group ? (
							<Link
								to='/settings/points-of-sale/groups/$groupUuid'
								params={{ groupUuid: pointOfSale.data.group.uuid }}
							>
								<Badge size='2xsmall'>{pointOfSale.data.group.name}</Badge>
							</Link>
						) : (
							L.Settings.PointOfSale.GroupNone()
						)
					}
				/>
				<SectionRow title={L.Settings.PointOfSale.Column.Identifier()} value={pointOfSale.data.identifier} />
			</Container>

			<Container>
				<ContainerHeader
					heading={L.Fields.SalesChannel()}
					rightSide={
						<Button size='small' variant='secondary' onClick={() => setLinkSalesChannelOpen(true)}>
							{L.Settings.PointOfSale.LinkSalesChannelButton()}
						</Button>
					}
				/>
				<Table
					className='w-full'
					data={pointOfSale.data.salesChannels}
					columnUtilities={{}}
					columnDefs={salesChannelColumnDefs}
					animateRows
					domLayout='autoHeight'
					rowSelection={{
						mode: 'singleRow',
						checkboxes: false,
						enableClickSelection: false,
						enableSelectionWithoutKeys: false
					}}
					suppressCellFocus
					rowToRouterConfig={sc => ({
						to: '/settings/sales-channels/$salesChannelUuid',
						params: { salesChannelUuid: sc.uuid }
					})}
					emptyState={
						<EmptyState
							title='No sales channels'
							description='This point of sale is not connected to any sales channels.'
							className='min-h-[120px]'
						/>
					}
				/>
			</Container>

			<LinkSalesChannelDrawer
				pointOfSaleUuid={pointOfSaleUuid}
				linkedSalesChannelUuids={pointOfSale.data.salesChannels.map(sc => sc.uuid)}
				open={linkSalesChannelOpen}
				onOpenChange={setLinkSalesChannelOpen}
			/>

			<LinkedDevicesPanel pointOfSaleUuid={pointOfSaleUuid} />

			<Container>
				<ContainerHeader
					heading={L.Settings.PointOfSale.ProductMenu.Title()}
					rightSide={
						<Link from={Route.fullPath} to='./product-menu'>
							<IconButton tooltipText={L.General.Edit()}>
								<PencilSquare />
							</IconButton>
						</Link>
					}
				/>
				<Text className='px-6 pb-4 text-sm text-ui-fg-subtle'>
					{L.Settings.PointOfSale.ProductMenu.Description()}
				</Text>
				{productGroups.data && productGroups.data.groups.length > 0 ? (
					<div className='flex flex-col divide-y divide-ui-border-base border-t border-ui-border-base'>
						{productGroups.data.groups.map(group => (
							<div key={group.uuid} className='flex items-center gap-x-3 px-6 py-3'>
								<div className='flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-md border border-ui-border-base bg-ui-bg-component'>
									{group.image ? (
										<AssetImage
											asset={group.image}
											className='size-full'
											preferredVariant='THUMBNAIL'
										/>
									) : group.icon ? (
										<ProductGroupGlyph name={group.icon} />
									) : (
										<Photo className='text-ui-fg-muted' />
									)}
								</div>
								<Text className='flex-1 truncate text-ui-fg-base'>{group.name}</Text>
								{group.isDefault && (
									<Badge color='blue' size='2xsmall'>
										{L.Settings.PointOfSale.ProductMenu.ShownIndividuallyBadge()}
									</Badge>
								)}
							</div>
						))}
					</div>
				) : (
					<EmptyState
						title={L.Settings.PointOfSale.ProductMenu.EmptyTitle()}
						description={L.Settings.PointOfSale.ProductMenu.EmptyDescription()}
						className='min-h-[120px]'
						action={{
							label: L.General.Edit(),
							onClick: () => navigate({ to: './product-menu' })
						}}
					/>
				)}
			</Container>
		</SingleColumnPage>
	)
}

function LinkSalesChannelDrawer({
	pointOfSaleUuid,
	linkedSalesChannelUuids,
	open,
	onOpenChange
}: {
	pointOfSaleUuid: string
	linkedSalesChannelUuids: string[]
	open: boolean
	onOpenChange: (open: boolean) => void
}) {
	const { L } = useI18n()
	const salesChannels = useListSalesChannels()
	const updatePointOfSale = useUpdatePointOfSale()

	const options = useMemo(
		() =>
			(salesChannels.data ?? [])
				.filter(sc => !linkedSalesChannelUuids.includes(sc.uuid))
				.map(sc => ({ value: sc.uuid, label: sc.name })),
		[salesChannels.data, linkedSalesChannelUuids]
	)

	const form = useAppForm({
		defaultValues: { salesChannelUuid: '' },
		onSubmit: async ({ value }) => {
			if (!value.salesChannelUuid) {
				return
			}
			const result = await updatePointOfSale({
				path: { pointOfSaleUuid },
				body: { salesChannelUuids: [...linkedSalesChannelUuids, value.salesChannelUuid] }
			})
			if (result.isSuccess) {
				toast.success(L.Settings.PointOfSale.LinkSalesChannelSuccess())
				onOpenChange(false)
				form.reset()
			} else {
				toast.error((result.response as any)?.message ?? L.Error.SomethingWentWrong())
			}
		}
	})

	return (
		<Drawer open={open} onOpenChange={onOpenChange}>
			<Drawer.Content>
				<form.AppForm>
					<Drawer.Header>
						<Drawer.Title asChild>
							<Heading>{L.Settings.PointOfSale.LinkSalesChannelTitle()}</Heading>
						</Drawer.Title>
						<Drawer.Description>{L.Settings.PointOfSale.LinkSalesChannelDescription()}</Drawer.Description>
					</Drawer.Header>
					<Drawer.Body>
						<FormContainer>
							<FormFieldGroup>
								{options.length === 0 && !salesChannels.isLoading ? (
									<Text size='small' className='text-ui-fg-muted'>
										{L.Settings.PointOfSale.NoUnlinkedSalesChannels()}
									</Text>
								) : (
									<form.AppField
										name='salesChannelUuid'
										validators={{
											onChange: ({ value }) => (!value ? L.General.Required() : undefined)
										}}
										children={field => (
											<field.SelectField
												label={L.Settings.PointOfSale.SelectSalesChannel()}
												options={options}
												optionsLoading={salesChannels.isLoading}
											/>
										)}
									/>
								)}
							</FormFieldGroup>
						</FormContainer>
					</Drawer.Body>
					<Drawer.Footer>
						<Button variant='secondary' size='small' onClick={() => onOpenChange(false)}>
							{L.General.Cancel()}
						</Button>
						<form.SaveButton label={L.Settings.PointOfSale.LinkSalesChannelButton()} />
					</Drawer.Footer>
				</form.AppForm>
			</Drawer.Content>
		</Drawer>
	)
}

function LinkDeviceDrawer({
	pointOfSaleUuid,
	open,
	onOpenChange
}: {
	pointOfSaleUuid: string
	open: boolean
	onOpenChange: (open: boolean) => void
}) {
	const { L } = useI18n()
	const devices = useListDevices()
	const linkDevice = useLinkDeviceToPointOfSale()

	// Only devices that aren't already operating as some point of sale can be linked here.
	const unlinkedOptions = useMemo(
		() => (devices.data ?? []).filter(d => !d.pointOfSale).map(d => ({ value: d.uuid, label: d.name })),
		[devices.data]
	)

	const form = useAppForm({
		defaultValues: { deviceUuid: '' },
		onSubmit: async ({ value }) => {
			if (!value.deviceUuid) {
				return
			}
			const result = await linkDevice({
				path: { deviceUuid: value.deviceUuid },
				body: { pointOfSaleUuid }
			})
			if (result.isSuccess) {
				toast.success(L.Settings.Devices.LinkSuccess())
				onOpenChange(false)
				form.reset()
			} else {
				toast.error((result.response as any)?.message ?? L.Error.SomethingWentWrong())
			}
		}
	})

	return (
		<Drawer open={open} onOpenChange={onOpenChange}>
			<Drawer.Content>
				<form.AppForm>
					<Drawer.Header>
						<Drawer.Title asChild>
							<Heading>{L.Settings.Devices.LinkDeviceTitle()}</Heading>
						</Drawer.Title>
						<Drawer.Description>{L.Settings.Devices.LinkDeviceDescription()}</Drawer.Description>
					</Drawer.Header>
					<Drawer.Body>
						<FormContainer>
							<FormFieldGroup>
								{unlinkedOptions.length === 0 ? (
									<Text size='small' className='text-ui-fg-muted'>
										{L.Settings.Devices.NoUnlinkedDevices()}
									</Text>
								) : (
									<form.AppField
										name='deviceUuid'
										validators={{
											onChange: ({ value }) => (!value ? L.General.Required() : undefined)
										}}
										children={field => (
											<field.SelectField
												label={L.Settings.Devices.SelectDevice()}
												options={unlinkedOptions}
												optionsLoading={devices.isLoading}
											/>
										)}
									/>
								)}
							</FormFieldGroup>
						</FormContainer>
					</Drawer.Body>
					<Drawer.Footer>
						<Button variant='secondary' size='small' onClick={() => onOpenChange(false)}>
							{L.General.Cancel()}
						</Button>
						<form.SaveButton label={L.Settings.Devices.LinkDeviceButton()} />
					</Drawer.Footer>
				</form.AppForm>
			</Drawer.Content>
		</Drawer>
	)
}

function LinkedDevicesPanel({ pointOfSaleUuid }: { pointOfSaleUuid: string }) {
	const { L } = useI18n()
	const prompt = usePrompt()
	const [linkOpen, setLinkOpen] = useState(false)
	const devices = useListDevices()
	const unlinkDevice = useUnlinkDeviceFromPointOfSale()

	const linkedDevices = useMemo(
		() => (devices.data ?? []).filter(d => d.pointOfSale?.uuid === pointOfSaleUuid),
		[devices.data, pointOfSaleUuid]
	)
	const hasLinkedDevice = linkedDevices.length > 0

	const handleUnlink = async (deviceUuid: string, deviceName: string) => {
		const confirmed = await prompt({
			variant: 'danger',
			title: L.Settings.Devices.UnlinkConfirmTitle(),
			description: L.Settings.Devices.UnlinkConfirmDescription(),
			verificationText: deviceName
		})
		if (!confirmed) {
			return
		}
		const result = await unlinkDevice({ path: { deviceUuid } })
		if (!result.isSuccess) {
			toast.error((result.response as any)?.message ?? L.Error.SomethingWentWrong())
			return
		}
		toast.success(L.Settings.Devices.UnlinkSuccess())
	}

	return (
		<Container>
			<ContainerHeader
				heading={L.Settings.Devices.LinkedDevicesTitle()}
				rightSide={
					<Tooltip content={L.Settings.Devices.LinkDeviceDisabledHint()} showTooltip={hasLinkedDevice}>
						<span className={hasLinkedDevice ? 'cursor-not-allowed' : undefined}>
							<Button
								size='small'
								variant='secondary'
								disabled={hasLinkedDevice}
								onClick={() => setLinkOpen(true)}
							>
								{L.Settings.Devices.LinkDeviceButton()}
							</Button>
						</span>
					</Tooltip>
				}
			/>
			{linkedDevices.length === 0 ? (
				<div className='px-6 pb-6'>
					<Text size='small' className='text-ui-fg-subtle'>
						{L.Settings.Devices.LinkedDevicesEmpty()}
					</Text>
				</div>
			) : (
				<div className='flex flex-col divide-y divide-ui-border-base border-t border-ui-border-base'>
					{linkedDevices.map(device => (
						<div key={device.uuid} className='flex items-center gap-x-3 px-6 py-3'>
							<Link
								to='/settings/devices/$deviceUuid'
								params={{ deviceUuid: device.uuid }}
								className='flex min-w-0 flex-1 flex-col'
							>
								<Text className='truncate text-ui-fg-base'>{device.name}</Text>
								<Text size='small' leading='compact' className='truncate font-mono text-ui-fg-subtle'>
									{device.identifier}
								</Text>
							</Link>
							<StatusBadge color={device.online ? 'green' : 'grey'}>
								{device.online ? L.Settings.DeviceStatus.Online() : L.Settings.DeviceStatus.Offline()}
							</StatusBadge>
							<Button
								size='small'
								variant='secondary'
								onClick={() => handleUnlink(device.uuid, device.name)}
							>
								{L.Settings.Devices.UnlinkButton()}
							</Button>
						</div>
					))}
				</div>
			)}
			<LinkDeviceDrawer pointOfSaleUuid={pointOfSaleUuid} open={linkOpen} onOpenChange={setLinkOpen} />
		</Container>
	)
}
